import * as React from 'react';
import ReactDOM from 'react-dom';
import * as _ from 'lodash';

import ArrowDropDownIcon from 'material-ui-icons/ArrowDropDown';
import ArrowDropUpIcon from 'material-ui-icons/ArrowDropUp';
import ClearIcon from 'material-ui-icons/Clear';
import {SvgIconProps} from 'material-ui';
import Highlighter from "react-highlight-words";

import VirtualizedSelect, {VirtualizedOptionRenderOptions} from 'react-virtualized-select';
import "react-select/dist/react-select.css";
import 'react-virtualized-select/styles.css';
import deepEqual from 'deep-equal';
import {ModelyearDto, TypefamilyDto, CnrDto, LanguageDto} from 'src/services/Client';
import {CSSProperties} from "react";
import {ArrowRendererProps, Option} from "react-select";
import {Flag} from "semantic-ui-react";
import {lang2flag} from "./MultiLineColumn";

/**
 * interface for parameter used in rendererOption
 */
interface IFilterOption<P = OptionValue> {
    /** option to be checked for filtering */
    option: P;
    /** current filter string */
    filter: string;
    /** is this option currently selected */
    selected: boolean;
}
/**
 * interface for parameter used in rendererValue
 */
interface IItemState<P = OptionValue> {
    /** current selected option */
    option: P;
    /** the current state is disabled, so use disabled colors */
    disabled?: boolean;
    /** index of currently selected option */
    index?: number;
}

// Possible parameters of the SelectField
export type OptionValue = object | string | number | boolean;
export type SelectedValue =
    {
        selectedValue: OptionValue;
        selectedIndex?: number;
    };

/** compares the 2 values to deside they are identical or not */
export type SelectFieldValueComparator = (actual: OptionValue, expected: OptionValue) => boolean;

export interface ISelectFieldProps {
    /** optional style to apply to the control */
    style?: React.CSSProperties;

    /** optional style to apply to the dropdown options */
    optionStyle?: CSSProperties;

    /** data source - array of objects, string, numbers or booleans */
    dataSource: Array<OptionValue>;
    /**
     * function which returns a custom way to render the value selected
     * @default <div>{rendererKey(option)}</div>
     */
    rendererValue?: React.ComponentType<IItemState>;
    /**
     * function which returns a custom way to render the options in the menu
     * @default <Highlighter
     *              searchWords={[filter]}
     *              textToHighlight={rendererKey(option)}
     *          />
     */
    rendererOption?: React.ComponentType<IFilterOption>;
    /**
     * method to filter a single option
     * @default RegEx search for filter in rendererKey(option)
     */
    filterOption?: (option: OptionValue, filter: string) => boolean;

    /**
     * Escape characters which are meaningful in regular expressions for filter highlighting
     */
    autoEscape?: boolean;

    /**
     * function which returns a custom way to render the value to key string
     * @default option.toString()
     */
    rendererKey?: (option: OptionValue, index?: number, para?: string) => string;

    /**
     * used to find the value in the dataSource (in value as Array<OptionValue> is applyed on element of array)
     *
     * @default deepEqual
     */
    valueComparator?: SelectFieldValueComparator;

    /**
     * field placeholder, displayed when there's no value
     * @default ""
     */
    placeholder?: string;
    /**
     * whether the Select is disabled or not
     * @default false
     */
    disabled?: boolean;
    /**
     * whether to enable searching feature or not
     * @default true;
     */
    searchable?: boolean;
    /**
     * whether it is possible to reset value. if enabled, an X button will appear at the right side.
     * @default true
     */
    clearable?: boolean;
    /**
     * whether it is possible to modify the selected value.
     * @default false
     */
    readonly?: boolean;
    /**
     * multi-value input
     * @default false
     */
    multi?: boolean;
    /**
     * open the options menu when the input gets focus (requires searchable = true)
     * @default false
     */
    openOnFocus?: boolean;
    /** initial field value, if multi = true, it's Array */
    value?: OptionValue | Array<OptionValue>;

    languageFlag?: boolean;

    /**
     * onChange handler.
     * If the property multi is set to true, the handler is (selectedValue: Array<SelectedValue>) => void.
     * If false, the handler is (selectedValue?: OptionValue, selectedIndex?: number) => void.
     */
    onChange?(selectedValue?: Array<SelectedValue> | OptionValue, selectedIndex?: number): void;

    boldFeaturePSLAssigned?: boolean;
    boldFeatureTechData?: boolean;
    id?: string;
}

type IValue =
    {
        index: number;
        origValue: OptionValue;
    };

type TValue = Option<IValue>;
interface ISelectFieldState {
    dataSource: Array<TValue>; // mapped data source
    selectedOption?: TValue | Array<TValue>; // current selected
    placeholder: string; // text shown for the empty SelectField
    filter: string; // current filter
    searchable: boolean; // defines if the input is possible or only selection
    focused: boolean; // is focused
    opened: boolean; // is opened
    boldFeature: boolean;
}

export default class SelectField extends React.Component<ISelectFieldProps, ISelectFieldState>
{
    private static isSearchable(props: ISelectFieldProps): boolean {
        return (props.searchable !== false && props.readonly !== true);
    }

    /**
     * converts the data source to array of TValues
     *
     * @private
     * @static
     * @param {Array<OptionValue>} dataSource origin data source to be converted
     * @param {(option: OptionValue, index?: number) => string} [rendererKey] function to convert origin value to string
     * @returns {Array<TValue>} internal representation of data source as array of TValues
     * @memberof SelectField
     */
    private static mapDatasource(dataSource: Array<OptionValue>, rendererKey?: (option: OptionValue, index?: number) => string): Array<TValue> {
        return dataSource.map(
            (value: OptionValue, index: number) => {
                const dispVal = rendererKey ? rendererKey(value, index) : value.toString();
                const v: TValue = {value: {index, origValue: value}, disabled: false, label: dispVal};
                return v;
            }
        );
    }

    /**
     * finds the origin value and maps it to TValue
     *
     * @private
     * @static
     * @param {Array<OptionValue>} dataSource origin data source
     * @param {(OptionValue | undefined)} value  origin value
     * @param {Array<TValue>} internalDataSource internal data source of TValues (corresponds to origin data source)
     * @returns {(TValue | undefined)} internal selected value corresponding to origin value
     * @memberof SelectField
     */
    private static mapValue(
        dataSource: Array<OptionValue>,
        value: OptionValue | undefined,
        internalDataSource: Array<TValue>,
        valueComparator?: SelectFieldValueComparator
    ): TValue | undefined {
        let i = value !== undefined ? _.findIndex(dataSource, val => {
            return valueComparator ? valueComparator(val, value) : deepEqual(val, value, {strict: true});
        }) : -1;
        return i < 0 ? undefined : internalDataSource[i];
    }

    private static mapValues(
        dataSource: Array<OptionValue>,
        values: Array<OptionValue> | undefined,
        internalDataSource: Array<TValue>,
        valueComparator?: SelectFieldValueComparator
    ): Array<TValue> | undefined {
        if (values !== undefined && values instanceof Array)
            return values.map(value => SelectField.mapValue(dataSource, value, internalDataSource, valueComparator)).filter(val => val !== undefined) as Array<TValue>;
        else
            return undefined;
    }

    constructor(props: ISelectFieldProps) {
        super(props);

        const dataSource = SelectField.mapDatasource(this.props.dataSource, this.props.rendererKey);

        this.state =
        {
            dataSource: dataSource,
            selectedOption: !props.multi ? SelectField.mapValue(props.dataSource, props.value as OptionValue | undefined, dataSource, props.valueComparator) :
                SelectField.mapValues(props.dataSource, props.value as Array<OptionValue> | undefined, dataSource, props.valueComparator),
            placeholder: props.placeholder ? props.placeholder : "",
            filter: "",
            searchable: SelectField.isSearchable(props),
            focused: false,
            opened: false,
            boldFeature: true,
        };
        // This binding is necessary to make `this` work in the callback
        this.renderOption = this.renderOption.bind(this);
        this.renderValue = this.renderValue.bind(this);
        this.setValue = this.setValue.bind(this);
        this.setValues = this.setValues.bind(this);
        this.inputChanged = this.inputChanged.bind(this);
        this.filterOption = this.filterOption.bind(this);
    }

    getInputNode(): HTMLInputElement | null {
        let node = ReactDOM.findDOMNode(this) as HTMLInputElement;
        if (node) {
            const listInputs = node.getElementsByTagName('input');
            if (listInputs.length > 0) {
                node = listInputs[0];
            }
        }
        return node;
    }

    componentWillReceiveProps(newProps: ISelectFieldProps) {
        // update only changed properties:
        let newState: ISelectFieldState = {...this.state}; // not deep clone object
        let dataSource = this.state.dataSource;
        if (newProps.dataSource !== this.props.dataSource || newProps.rendererKey !== this.props.rendererKey) {
            newState.dataSource = SelectField.mapDatasource(newProps.dataSource, newProps.rendererKey);
            dataSource = newState.dataSource;
        }
        // if the initial value is not changed, use the current selected value on new data source too
        if (newProps.multi === true) // multi selection
        {
            const selectedOption: Array<TValue> | undefined = this.state.selectedOption as Array<TValue>;

            const curVals = this.props.multi !== newProps.multi || newProps.value !== this.props.value ? newProps.value :
                (selectedOption ? selectedOption.map(value => value ? (value.value ? value.value.origValue : undefined) : undefined)
                    .filter(origValue => origValue !== undefined) : undefined);

            if (newProps.value !== this.props.value || dataSource !== this.state.dataSource) {
                newState.selectedOption = SelectField.mapValues(newProps.dataSource, curVals as Array<OptionValue>, dataSource, this.props.valueComparator);
                // if not found current selection, try to use initial values
                if (newState.selectedOption === undefined && curVals !== newProps.value) {
                    newState.selectedOption = SelectField.mapValues(newProps.dataSource, newProps.value as Array<OptionValue>, dataSource, this.props.valueComparator);
                }
            }
        }
        else // single value
        {
            const selectedOption: TValue | undefined = this.state.selectedOption;

            const curVal = this.props.multi !== newProps.multi || newProps.value !== this.props.value ? newProps.value :
                (selectedOption && selectedOption.value ? selectedOption.value.origValue : undefined);

            if (newProps.value !== this.props.value || dataSource !== this.state.dataSource) {
                newState.selectedOption = SelectField.mapValue(newProps.dataSource, curVal, dataSource, this.props.valueComparator);
                // if not found current selection, try to use initial value
                if (newState.selectedOption === undefined && curVal !== newProps.value) {
                    newState.selectedOption = SelectField.mapValue(newProps.dataSource, newProps.value, dataSource, this.props.valueComparator);
                }
            }
        }

        if (newProps.placeholder !== undefined && newProps.placeholder !== this.props.placeholder) {
            newState.placeholder = newProps.placeholder;
        }
        if (newProps.readonly !== this.props.readonly || newProps.searchable !== this.props.searchable) {
            newState.searchable = SelectField.isSearchable(newProps);
        }
        this.setState(newState);
    }

    setValue(newValue: TValue) {
        if (!this.props.readonly) {
            this.setState({selectedOption: newValue, filter: ""}, () => {
                if (this.props.onChange) {
                    if (newValue && newValue.value)
                        this.props.onChange(newValue.value.origValue, newValue.value.index);
                    else
                        this.props.onChange(undefined, undefined);
                }
            }
            );
        }
    }

    setValues(newValues: Array<TValue>) {
        if (!this.props.readonly) {
            this.setState({selectedOption: newValues, filter: ""}, () => {
                if (this.props.onChange) {
                    const selVals = newValues.map((newValue: TValue) => {
                        if (newValue.value) {
                            const value: SelectedValue =
                            {
                                selectedValue: newValue.value.origValue,
                                selectedIndex: newValue.value.index
                            };
                            return value;
                        }
                        else {
                            return undefined;
                        }
                    }
                    ).filter(value => value !== undefined) as SelectedValue[];

                    this.props.onChange(selVals);
                }
            }
            );
        }
    }

    renderOption(opt: VirtualizedOptionRenderOptions<TValue>): JSX.Element {
        const {option, valueArray} = opt;
        const selected = valueArray.indexOf(option) >= 0;

        let rendered: any;
        if (this.props.rendererOption && option.value) {
            const Renderer = this.props.rendererOption;
            rendered = (
                <Renderer
                    filter={this.state.filter}
                    option={option.value.origValue}
                    selected={selected}
                />
            );
        }
        else {
            rendered = (
                <div style={this.props.optionStyle}>
                    {
                        this.props.languageFlag && option.value &&
                        <Flag name={lang2flag((option.value.origValue as LanguageDto).vmce)} className="SelectField-Item-Value-LanguageFlag"/>
                    }
                    <Highlighter
                        searchWords={[this.state.filter]}
                        autoEscape={this.props.autoEscape}
                        textToHighlight={option.label ? option.label : ""}
                    />
                </div>
            );
        }

        let style = {...opt.style};

        let className = "SelectField-Item";
        if (option.value && this.isBold(option.value.origValue)) {
            className = "SelectField-Item bold";
        }

        return (
            <div
                key={opt.key}
                id={this.props.id}
                onClick={() => opt.selectValue(opt.option)}
                onMouseEnter={() => opt.focusOption(opt.option)}
                className={className}
                style={style}
            >
                {rendered}
            </div>
        );
    }

    isBold = (arg: any): arg is ModelyearDto | TypefamilyDto | CnrDto => {
        return (this.props.boldFeaturePSLAssigned !== undefined && this.props.boldFeaturePSLAssigned === true &&
            arg.hasPSLAssigned !== undefined && arg.hasPSLAssigned === true) ||
            (this.props.boldFeatureTechData !== undefined && this.props.boldFeatureTechData === true && arg.hasTechData);
    }

    renderValue(option: TValue): JSX.Element {
        if (this.props.rendererValue && option.value) {
            const Renderer = this.props.rendererValue;
            return <Renderer option={option.value.origValue} disabled={this.props.disabled ? this.props.disabled : false} index={option.value.index} />;
        }
        else {
            return (
                <div className="SelectField-Item-Value">
                    {
                        this.props.languageFlag && option.value &&
                        <Flag name={lang2flag((option.value.origValue as LanguageDto).vmce)} className="SelectField-Item-Value-LanguageFlag"/>
                    }
                    {option.label}
                </div>);
        }
    }

    inputChanged(inputValue: string): void {
        this.setState({filter: inputValue});
    }

    filterOption(option: TValue, filter: string): boolean {
        if (this.props.filterOption && option.value)
            return this.props.filterOption(option.value.origValue, filter);
        else if (option.label) {
            try {
                const f = filter.toLowerCase(); // RexExp
                return option.label.toLowerCase().search(f) !== -1;
            }
            catch
            {
                return false;
            }
        }
        else
            return false;
    }

    render() {
        const color = "#9e9e9e";
        const clearIconProps: SvgIconProps =
        {
            color: color,
            hoverColor: "white",
        };

        const arrowIconProps: SvgIconProps =
        {
            color: color,
            hoverColor: "white",
        };

        const {
            readonly = false,
            disabled = false,
            clearable = true,
            multi = false,
            openOnFocus = false,
        } = this.props;

        return (
            <div id={this.props.id}>
                <VirtualizedSelect
                    // icons
                    arrowRenderer={(arrowProps: ArrowRendererProps) => {
                        return disabled ? <></> :
                            this.state.opened && !readonly ? <ArrowDropUpIcon {...arrowIconProps} /> : <ArrowDropDownIcon {...arrowIconProps} />;
                    }}
                    clearRenderer={() => <ClearIcon {...clearIconProps} />}

                    className="SelectField"

                    noResultsText=""
                    // isLoading={true}

                    // props dependent
                    disabled={disabled}
                    clearable={clearable && !readonly}
                    multi={multi}
                    openOnFocus={openOnFocus}
                    style={this.props.style}
                    // state dependent
                    options={readonly ? undefined : this.state.dataSource}
                    placeholder={this.state.placeholder}
                    value={this.state.selectedOption}
                    searchable={this.state.searchable}

                    // internal
                    onChange={!multi ? this.setValue : this.setValues}
                    onInputChange={this.inputChanged}
                    optionRenderer={this.renderOption}
                    valueRenderer={this.renderValue}
                    filterOption={this.filterOption}

                    // get state of control
                    onFocus={() => this.setState({focused: true})}
                    onBlur={() => this.setState({focused: false})}
                    onOpen={() => this.setState({opened: true})}
                    onClose={() => this.setState({opened: false})}
                />
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/SelectField.tsx